/**
 * /recensione/<token> — the customer's review page, shared by both sites.
 *
 * The backend sends `https://<sede-domain>/recensione/<token>` (spec §5);
 * App.tsx matches the path before the site renders, so the page is
 * client-rendered and branded from `sede`. API: §4, via ./api (cross-origin
 * to backendBaseUrl, like the contact form).
 *
 * Hard rules (spec §2, research §1.4/§2.6/§2.7): no gating — the Google
 * invitation is the same for every respondent and NOTHING in this file
 * branches on the rating; no pre-filled review text; publication needs the
 * separate, unticked consent box; `website` is the honeypot.
 */
import { useEffect, useId, useReducer, useRef, useState, type FormEvent, type KeyboardEvent, type ReactNode, type RefObject } from 'react'
import type { SedeConfig } from '../sedi/types'
import { fetchReviewRequest, googleRedirectUrl, optOut, submitReview, withdrawConsent, type ReviewRequestInfo } from './api'
import {
  NAME_MAX,
  TEXT_COUNTER_FROM,
  TEXT_MAX,
  canSubmit,
  initialPageState,
  pageReducer,
  ratingForKey,
  signature,
  type PageView,
  type WithdrawStep,
} from './model'
import { PAGE } from './strings'
import { ReviewChrome, privacyHref, useNoindexHead } from './ReviewChrome'
import { copyText } from './clipboard'

const STAR_PATH = 'M12 2.6l2.83 5.73 6.32.92-4.57 4.46 1.08 6.29L12 17.03l-5.66 2.97 1.08-6.29L2.85 9.25l6.32-.92L12 2.6z'

export default function ReviewPage({ token, sede }: { token: string; sede: SedeConfig }) {
  const [state, dispatch] = useReducer(pageReducer, typeof window !== 'undefined' ? window.location.hash : '', initialPageState)
  useNoindexHead(`${PAGE.kicker} · ${sede.brand.name}`)

  useEffect(() => {
    let cancelled = false
    fetchReviewRequest(token).then((result) => {
      if (!cancelled) dispatch({ type: 'loaded', result })
    })
    return () => {
      cancelled = true
    }
  }, [token, state.reload])

  // Move focus to the new heading after a user-driven change of view, so a
  // screen reader hears the result (the first load keeps the browser's focus).
  const headingRef = useRef<HTMLHeadingElement>(null)
  const viewKey = `${state.view.kind}:${state.optOut}`
  const firstKey = useRef(true)
  useEffect(() => {
    if (state.view.kind === 'loading') return
    if (firstKey.current) {
      firstKey.current = false
      return
    }
    headingRef.current?.focus()
  }, [viewKey, state.view.kind])

  const info = 'info' in state.view ? state.view.info : null
  const sedeName = info?.sede.name || sede.brand.name

  const setOptOut = (step: 'idle' | 'confirm') => {
    if (step === 'idle' && window.location.hash === '#stop') {
      history.replaceState(null, '', window.location.pathname + window.location.search)
    }
    dispatch({ type: 'optOut', step })
  }
  const confirmOptOut = async () => {
    dispatch({ type: 'optOut', step: 'sending' })
    const r = await optOut(token)
    dispatch({ type: 'optOutResult', ok: r.ok, status: r.status })
  }

  const footerAction =
    state.view.kind !== 'notFound' && state.optOut === 'idle' ? (
      <button type="button" onClick={() => setOptOut('confirm')}>
        {PAGE.stopLink}
      </button>
    ) : null

  return (
    <ReviewChrome sede={sede} privacyUrl={info?.sede.privacy_url} footerAction={footerAction}>
      <div className="rf-card">
        {state.optOut !== 'idle' ? (
          <OptOutPanel
            step={state.optOut}
            sedeName={sedeName}
            privacyEmail={sede.contact.privacyEmail}
            headingRef={headingRef}
            onConfirm={confirmOptOut}
            onCancel={() => setOptOut('idle')}
          />
        ) : (
          <ViewBody
            view={state.view}
            sede={sede}
            sedeName={sedeName}
            token={token}
            headingRef={headingRef}
            submitting={state.submitting}
            submitError={state.submitError}
            submitDetail={state.submitDetail}
            onRetry={() => dispatch({ type: 'reload' })}
            withdraw={state.withdraw}
            privacyEmail={sede.contact.privacyEmail}
            onWithdrawStep={(step) => dispatch({ type: 'withdraw', step })}
            onWithdraw={async () => {
              dispatch({ type: 'withdraw', step: 'sending' })
              const r = await withdrawConsent(token)
              dispatch({ type: 'withdrawResult', ok: r.ok, status: r.status })
            }}
            onSubmit={async (body, sent) => {
              dispatch({ type: 'submit' })
              const result = await submitReview(token, body)
              dispatch({ type: 'submitted', result, sent })
            }}
          />
        )}
      </div>
    </ReviewChrome>
  )
}

type HeadingRef = RefObject<HTMLHeadingElement | null>

function Heading({ headingRef, children }: { headingRef: HeadingRef; children: ReactNode }) {
  return (
    <h1 ref={headingRef} tabIndex={-1} className="rv-title">
      {children}
    </h1>
  )
}

function Contacts({ sede }: { sede: SedeConfig }) {
  const phone = sede.contact.phone
  return (
    <div className="rv-contacts">
      {phone && <a href={`tel:${phone.replace(/\s+/g, '')}`}>{phone}</a>}
      <a href={`mailto:${sede.contact.email}`}>{sede.contact.email}</a>
    </div>
  )
}

function ViewBody({
  view,
  sede,
  sedeName,
  token,
  headingRef,
  submitting,
  submitError,
  submitDetail,
  onRetry,
  withdraw,
  privacyEmail,
  onWithdrawStep,
  onWithdraw,
  onSubmit,
}: {
  view: PageView
  sede: SedeConfig
  sedeName: string
  token: string
  headingRef: HeadingRef
  submitting: boolean
  submitError: 'network' | 'invalid' | 'throttled' | null
  submitDetail: string
  onRetry: () => void
  withdraw: WithdrawStep
  privacyEmail: string
  onWithdrawStep: (step: 'idle' | 'confirm') => void
  onWithdraw: () => void
  onSubmit: Parameters<typeof ReviewForm>[0]['onSubmit']
}) {
  switch (view.kind) {
    case 'loading':
      return (
        <div className="rv-center" aria-busy="true" style={{ padding: '32px 0' }}>
          <span className="rf-spinner rf-accent-text" aria-hidden="true" />
          <p className="rv-p" style={{ marginTop: 12 }} role="status">
            {PAGE.loading}
          </p>
        </div>
      )
    case 'network':
      return (
        <div className="rv-sections">
          <div>
            <Heading headingRef={headingRef}>{PAGE.networkTitle}</Heading>
            <p className="rv-lead">{PAGE.networkBody}</p>
          </div>
          <button type="button" className="rf-btn rv-block" onClick={onRetry}>
            {PAGE.retry}
          </button>
        </div>
      )
    case 'notFound':
      return (
        <div>
          <Heading headingRef={headingRef}>{PAGE.notFoundTitle}</Heading>
          <p className="rv-lead">{PAGE.notFoundBody}</p>
          <Contacts sede={sede} />
        </div>
      )
    case 'expired':
    case 'revoked':
      return (
        <div>
          <Heading headingRef={headingRef}>{view.kind === 'expired' ? PAGE.expiredTitle : PAGE.revokedTitle}</Heading>
          <p className="rv-lead">{view.kind === 'expired' ? PAGE.expiredBody : PAGE.revokedBody(sedeName)}</p>
          <p className="rv-p" style={{ marginTop: 16 }}>
            {PAGE.contactUs}
          </p>
          <Contacts sede={sede} />
        </div>
      )
    case 'open':
      return (
        <ReviewForm
          info={view.info}
          sede={sede}
          headingRef={headingRef}
          submitting={submitting}
          submitError={submitError}
          submitDetail={submitDetail}
          onSubmit={onSubmit}
        />
      )
    case 'submitted':
      return (
        <div className="rv-sections">
          <div>
            <div className="rf-success-icon" aria-hidden="true">
              <svg width="28" height="28" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
                <path d="M4.5 12.75l6 6 9-13.5" />
              </svg>
            </div>
            <div className="rv-center">
              <Heading headingRef={headingRef}>{PAGE.thanksTitle(view.info?.first_name ?? '')}</Heading>
              <p className="rv-lead">
                {PAGE.thanksReceived}{' '}
                {!view.consent
                  ? PAGE.thanksPrivate
                  : view.published
                    ? PAGE.thanksPublic(signature(view.displayName, view.info?.town ?? ''))
                    : PAGE.thanksNotVisible}
              </p>
            </div>
          </div>
          {view.google && <GoogleInvite token={token} text={view.text} />}
          {(view.consent || withdraw === 'done') && (
            <WithdrawConsent step={withdraw} privacyEmail={privacyEmail} onStep={onWithdrawStep} onConfirm={onWithdraw} />
          )}
        </div>
      )
  }
}

/* ── The form ─────────────────────────────────────────────────────────── */

function ReviewForm({
  info,
  sede,
  headingRef,
  submitting,
  submitError,
  submitDetail,
  onSubmit,
}: {
  info: ReviewRequestInfo
  sede: SedeConfig
  headingRef: HeadingRef
  submitting: boolean
  submitError: 'network' | 'invalid' | 'throttled' | null
  submitDetail: string
  onSubmit: (
    body: { rating: number; text: string; display_name?: string; consent_publish: boolean; website: string },
    sent: { text: string; consent: boolean; displayName: string },
  ) => void
}) {
  const ids = useId()
  const [rating, setRating] = useState(0)
  const [hover, setHover] = useState(0)
  const [text, setText] = useState('')
  const [name, setName] = useState(info.display_name)
  const [consent, setConsent] = useState(false)
  const [website, setWebsite] = useState('')
  const [ratingError, setRatingError] = useState(false)
  const starRefs = useRef<(HTMLButtonElement | null)[]>([])

  const shownName = name.trim() || info.display_name
  const domain = sede.domains[0] ?? sede.brand.name

  const choose = (n: number, focus = false) => {
    setRating(n)
    setRatingError(false)
    if (focus) starRefs.current[n - 1]?.focus()
  }
  const onStarKey = (e: KeyboardEvent<HTMLButtonElement>) => {
    const next = ratingForKey(e.key, rating)
    if (next === null) return
    e.preventDefault()
    choose(next, true)
  }

  const submit = (e: FormEvent) => {
    e.preventDefault()
    if (submitting) return
    if (!canSubmit(rating)) {
      setRatingError(true)
      starRefs.current[0]?.focus()
      return
    }
    const trimmed = text.trim()
    const customName = name.trim()
    onSubmit(
      {
        rating,
        text: trimmed,
        ...(customName ? { display_name: customName } : {}),
        consent_publish: consent,
        website,
      },
      { text: trimmed, consent, displayName: shownName },
    )
  }

  const lit = hover || rating
  const errorText =
    submitError === 'invalid' ? submitDetail || PAGE.submitInvalid : submitError === 'throttled' ? PAGE.submitThrottled : submitError === 'network' ? PAGE.submitError : ''

  return (
    <form onSubmit={submit} noValidate className="rv-sections">
      <div>
        <p className="rv-kicker">{PAGE.kicker}</p>
        <Heading headingRef={headingRef}>{PAGE.greeting(info.first_name)}</Heading>
        <p className="rv-lead">{PAGE.intro}</p>
      </div>

      {/* Honeypot — invisible to people, same shape as the contact form's. */}
      <div aria-hidden="true" style={{ position: 'absolute', left: '-9999px', top: '-9999px', opacity: 0, height: 0, overflow: 'hidden' }}>
        <label htmlFor={`${ids}-website`}>Website</label>
        <input id={`${ids}-website`} name="website" type="text" tabIndex={-1} autoComplete="off" value={website} onChange={(e) => setWebsite(e.target.value)} />
      </div>

      <div>
        <p id={`${ids}-rating`} className="rf-label">
          {PAGE.ratingLabel} <span className="rf-req" aria-hidden="true">*</span>
        </p>
        <div
          role="radiogroup"
          aria-labelledby={`${ids}-rating`}
          aria-describedby={`${ids}-rating-msg`}
          aria-required="true"
          aria-invalid={ratingError || undefined}
          className={`rv-stars${ratingError ? ' is-error' : ''}`}
          onMouseLeave={() => setHover(0)}
        >
          {[1, 2, 3, 4, 5].map((n) => (
            <button
              key={n}
              ref={(el) => {
                starRefs.current[n - 1] = el
              }}
              type="button"
              role="radio"
              aria-checked={rating === n}
              aria-label={PAGE.ratingStar(n)}
              tabIndex={(rating || 1) === n ? 0 : -1}
              className={`rv-star${n <= lit ? ' is-on' : ''}`}
              onClick={() => choose(n)}
              onKeyDown={onStarKey}
              onMouseEnter={() => setHover(n)}
            >
              <svg viewBox="0 0 24 24" aria-hidden="true" focusable="false">
                <path
                  d={STAR_PATH}
                  fill={n <= lit ? 'currentColor' : 'none'}
                  stroke="currentColor"
                  strokeWidth="1.6"
                  strokeLinejoin="round"
                />
              </svg>
            </button>
          ))}
        </div>
        <p id={`${ids}-rating-msg`} className={ratingError ? 'rf-error' : 'rv-chosen'} aria-live="polite" style={ratingError ? { fontSize: 14 } : undefined}>
          {ratingError ? PAGE.ratingRequired : rating ? PAGE.ratingChosen(rating) : PAGE.ratingHint}
        </p>
      </div>

      <div>
        <div className="rv-label-row">
          <label htmlFor={`${ids}-text`} className="rf-label">
            {PAGE.textLabel}
          </label>
          <span className="rv-optional">{PAGE.textOptional}</span>
        </div>
        <textarea
          id={`${ids}-text`}
          className="rf-field"
          rows={5}
          maxLength={TEXT_MAX}
          value={text}
          onChange={(e) => setText(e.target.value.slice(0, TEXT_MAX))}
          aria-describedby={`${ids}-text-hint ${ids}-text-count`}
          lang="it"
        />
        <p id={`${ids}-text-hint`} className="rv-hint">
          {PAGE.textHint}
        </p>
        <p id={`${ids}-text-count`} className={`rv-count${text.length >= TEXT_MAX ? ' is-max' : ''}`} aria-live="polite">
          {text.length >= TEXT_COUNTER_FROM ? PAGE.textCount(text.length, TEXT_MAX) : ''}
        </p>
      </div>

      <div>
        <label htmlFor={`${ids}-name`} className="rf-label">
          {PAGE.nameLabel}
        </label>
        <input
          id={`${ids}-name`}
          className="rf-field"
          type="text"
          maxLength={NAME_MAX}
          autoComplete="off"
          value={name}
          onChange={(e) => setName(e.target.value)}
          aria-describedby={`${ids}-name-hint`}
        />
        <p id={`${ids}-name-hint`} className="rv-hint">
          {PAGE.namePreview(signature(shownName, info.town), !!info.town.trim())}
        </p>
      </div>

      <div>
        <label className="rv-consent">
          <input type="checkbox" className="rf-check" checked={consent} onChange={(e) => setConsent(e.target.checked)} />
          <span>{PAGE.consent(domain, signature(shownName, info.town), !!info.town.trim())}</span>
        </label>
        <p className="rv-consent-note">
          {PAGE.consentNote}{' '}
          <a href={privacyHref(sede, info.sede.privacy_url)}>{PAGE.privacy}</a>
        </p>
      </div>

      <div>
        {errorText && (
          <p className="rf-error-banner" role="alert">
            {errorText}
          </p>
        )}
        <button type="submit" className="rf-btn rv-block" disabled={submitting} aria-disabled={submitting || undefined}>
          {submitting && <span className="rf-spinner" aria-hidden="true" />}
          {submitting ? PAGE.submitting : PAGE.submit}
        </button>
      </div>
    </form>
  )
}

/* ── «Pubblica anche su Google» — identical for every respondent ─────── */

function GoogleInvite({ token, text }: { token: string; text: string }) {
  const ids = useId()
  const [copy, setCopy] = useState<'idle' | 'copied' | 'failed'>('idle')
  const hasText = text.trim().length > 0

  // The click copies (only now, never before) and the link itself opens the
  // tracked redirect in a new tab: a real link, so no popup blocker gets a say.
  const onClick = () => {
    if (!hasText) return
    copyText(text).then((ok) => setCopy(ok ? 'copied' : 'failed'))
  }

  return (
    <section className="rf-softbox" aria-labelledby={`${ids}-g`}>
      <h2 id={`${ids}-g`} className="rf-head">
        {PAGE.googleTitle}
      </h2>
      <p className="rv-p">{PAGE.googleBody}</p>
      <a
        className="rf-btn rv-block"
        href={googleRedirectUrl(token)}
        target="_blank"
        rel="noopener noreferrer"
        onClick={onClick}
        aria-describedby={hasText ? `${ids}-note` : undefined}
      >
        {hasText ? PAGE.googleButtonCopy : PAGE.googleButtonOpen}
        <span className="sr-only"> {PAGE.newTab}</span>
        <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
          <path d="M14 4h6v6M20 4l-9 9M18 14v5a1 1 0 0 1-1 1H5a1 1 0 0 1-1-1V7a1 1 0 0 1 1-1h5" />
        </svg>
      </a>
      {hasText && (
        <p id={`${ids}-note`} className="rv-hint">
          {PAGE.googleCopyNote}
        </p>
      )}
      <div role="status" aria-live="polite">
        {copy === 'copied' && <p className="rv-status">{PAGE.googleCopied}</p>}
        {copy === 'failed' && <p className="rv-hint">{PAGE.googleCopyFailed}</p>}
      </div>
      {copy === 'failed' && (
        <>
          <label htmlFor={`${ids}-text`} className="sr-only">
            {PAGE.googleTextLabel}
          </label>
          <textarea id={`${ids}-text`} className="rf-field rv-copybox" readOnly value={text} onFocus={(e) => e.currentTarget.select()} lang="it" />
        </>
      )}
    </section>
  )
}

/* ── Withdrawing the publication consent (only when it was given) ──── */

/**
 * A quiet footer line under the thank-you and the Google block, the weight of
 * the opt-out link: it must never compete with either. After the withdrawal
 * the lead above already reads «not published» (view.consent is false); this
 * line confirms it.
 */
function WithdrawConsent({
  step,
  privacyEmail,
  onStep,
  onConfirm,
}: {
  step: WithdrawStep
  privacyEmail: string
  onStep: (step: 'idle' | 'confirm') => void
  onConfirm: () => void
}) {
  const ids = useId()
  const confirmRef = useRef<HTMLButtonElement>(null)
  useEffect(() => {
    if (step === 'confirm') confirmRef.current?.focus()
  }, [step])

  if (step === 'done') {
    return (
      <p className="rv-quiet" role="status">
        {PAGE.withdrawDone}
      </p>
    )
  }
  if (step === 'idle') {
    return (
      <div className="rv-quiet">
        <button type="button" className="rv-quiet-link" onClick={() => onStep('confirm')}>
          {PAGE.withdrawLink}
        </button>
      </div>
    )
  }
  const sending = step === 'sending'
  return (
    <div className="rv-quiet" role="group" aria-labelledby={`${ids}-wt`}>
      <p id={`${ids}-wt`} className="rv-quiet-title">
        {PAGE.withdrawTitle}
      </p>
      <p>{PAGE.withdrawBody}</p>
      {step === 'error' && (
        <p className="rf-danger-text" role="alert">
          {PAGE.withdrawError(privacyEmail)}
        </p>
      )}
      <div className="rv-quiet-actions">
        <button ref={confirmRef} type="button" className="rv-quiet-link" onClick={onConfirm} disabled={sending}>
          {sending ? PAGE.withdrawSending : PAGE.withdrawConfirm}
        </button>
        <button type="button" className="rv-quiet-link" onClick={() => onStep('idle')} disabled={sending}>
          {PAGE.withdrawCancel}
        </button>
      </div>
    </div>
  )
}

/* ── Opt-out ──────────────────────────────────────────────────────────── */

function OptOutPanel({
  step,
  sedeName,
  privacyEmail,
  headingRef,
  onConfirm,
  onCancel,
}: {
  step: 'confirm' | 'sending' | 'done' | 'error' | 'invalid' | 'idle'
  sedeName: string
  privacyEmail: string
  headingRef: HeadingRef
  onConfirm: () => void
  onCancel: () => void
}) {
  if (step === 'done' || step === 'invalid') {
    return (
      <div className="rv-sections">
        <div>
          <Heading headingRef={headingRef}>{step === 'done' ? PAGE.stopDoneTitle : PAGE.notFoundTitle}</Heading>
          <p className="rv-lead">{step === 'done' ? PAGE.stopDoneBody(sedeName) : PAGE.stopInvalid}</p>
        </div>
        {step === 'done' && (
          <button type="button" className="rf-btn rf-btn-ghost rv-block" onClick={onCancel}>
            {PAGE.backToPage}
          </button>
        )}
      </div>
    )
  }
  const sending = step === 'sending'
  return (
    <div className="rv-sections">
      <div>
        <Heading headingRef={headingRef}>{PAGE.stopTitle}</Heading>
        <p className="rv-lead">{PAGE.stopBody(sedeName)}</p>
      </div>
      {step === 'error' && (
        <p className="rf-error-banner" role="alert" style={{ marginBottom: 0 }}>
          {PAGE.stopError(privacyEmail)}
        </p>
      )}
      <div className="rv-actions">
        <button type="button" className="rf-btn" onClick={onConfirm} disabled={sending}>
          {sending && <span className="rf-spinner" aria-hidden="true" />}
          {sending ? PAGE.stopSending : PAGE.stopConfirm}
        </button>
        <button type="button" className="rf-btn rf-btn-ghost" onClick={onCancel} disabled={sending}>
          {PAGE.stopCancel}
        </button>
      </div>
    </div>
  )
}
